import React, { memo, useCallback, useEffect, useState } from "react";
import { OfficeUserStepProps } from "./types";
import { useTranslate } from "helpers";
import { UserRow } from "services/api/users/types";
import { deleteUserAPI } from "services/api/users/api";
import { getOfficeUsersAPI } from "services/api/offices/api";
import { Button } from "ui/components/Button/Button";
import { Dialog } from "ui/components/Dialog";
import { ZPosition } from "ui/components/Modal/types";
import { TableSearch } from "ui/components/Table/TableSearch";
import UserEditCard from "ui/modules/users/pages/UserDetailPage/components/UserDetailCard/components/UserEditCard/UserEditCard";
import { OFFICE_USER_COLUMNS } from "./consts";

const OfficeUserStep: React.FC<OfficeUserStepProps> = ({
  formValues: parentFormValues,
  tenantId,
}) => {
  const officeId = parentFormValues.id;

  const { t } = useTranslate();

  const [users, setUsers] = useState<UserRow[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  const [deleteId, setDeleteId] = useState<string>("");
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const fetchUsers = useCallback(async () => {
    if (!officeId) return;

    setIsLoading(true);

    try {
      const result = await getOfficeUsersAPI(officeId);
      setUsers(result);
    } catch {
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, [officeId]);

  useEffect(() => {
    if (officeId) fetchUsers();
  }, [officeId, fetchUsers]);

  const handleAddUser = useCallback(() => {
    setUserId(null);
    setShowUserModal(true);
  }, []);

  const handleRowClick = useCallback((id: string) => {
    setUserId(id);
    setShowUserModal(true);
  }, []);

  const handleUserSuccess = useCallback(() => {
    setShowUserModal(false);
    setUserId(null);
    fetchUsers();
  }, [fetchUsers]);

  const handleUserClose = useCallback(() => {
    setShowUserModal(false);
    setUserId(null);
  }, []);

  /*----------------------------------------------------------------
      BRISANJE
  ----------------------------------------------------------------*/

  // Klik na koš ne smije otvoriti i uređivanje retka ispod njega.
  const handleDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, id: string) => {
      event.stopPropagation();
      event.preventDefault();

      setDeleteId(id);
      setOpenDialog(true);
    },
    []
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteId) return;

    try {
      await deleteUserAPI(deleteId, t);
      await fetchUsers();
    } finally {
      setOpenDialog(false);
      setDeleteId("");
    }
  }, [deleteId, t, fetchUsers]);

  const handleDeleteCancel = useCallback(() => {
    setOpenDialog(false);
    setDeleteId("");
  }, []);

  return (
    <div className="flex items-center justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
      <div className="w-full max-w-4xl">
        <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">
          Korisnici
        </h1>
        <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
          Dodajte korisnike koji pripadaju ovoj poslovnici. Nakon unosa možete
          nastaviti na sljedeći korak.
        </p>

        {/* Uređivanje ide klikom na redak; prazno stanje nudi samo unos. */}
        {!isLoading && users.length === 0 ? (
          <div className="max-w-md mx-auto">
            <Button
              variant="primary"
              size="full"
              children="Dodaj korisnika"
              startIcon="/media/icons/plus.svg"
              onClick={handleAddUser}
            />
          </div>
        ) : (
          <>
            <div className="flex justify-end mb-3">
              <Button
                variant="primary"
                children="Dodaj korisnika"
                startIcon="/media/icons/plus.svg"
                onClick={handleAddUser}
              />
            </div>

            <TableSearch
              columns={OFFICE_USER_COLUMNS(handleDeleteClick)}
              data={users}
              selectableRows={false}
              onRowClick={handleRowClick}
              isLoading={isLoading}
              disablePagination
            />
          </>
        )}
      </div>

      {/*
        Otvara se nad modalom poslovnice, pa mu treba viši sloj: modal s uvodnom plohom
        Modal prisilno stavlja na dno, a poslovnica je na sredini i pokrila bi ga.
      */}
      <UserEditCard
        userId={userId}
        tenantId={tenantId}
        officeId={officeId}
        show={showUserModal}
        isFullScreen={false}
        zPosition={ZPosition.Top}
        onSuccess={handleUserSuccess}
        onClose={handleUserClose}
      />

      {/*
        Ovdje se briše sam korisnik, ne samo njegova veza s poslovnicom — poslovnica stoji
        kao polje na korisniku, pa zasebnog odvezivanja nema. Poruka to kaže izravno.
      */}
      <Dialog
        open={openDialog}
        title="Brisanje korisnika"
        message="Korisnik se briše iz sustava, ne samo iz ove poslovnice. Više se neće moći prijaviti."
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </div>
  );
};

export default memo(OfficeUserStep);
